import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useGrowStore, calcPointsFromScore, type ClassResult } from "../../../hooks/useGrowStore";
import { useReviewStore } from "../../../hooks/useReviewStore";
import ReflectionModal from "./ReflectionModal";

interface Props {
  prompt: string;
  classId: number;
  classTitle: string;
}

const SCORE_ITEMS = [
  { key: "creativity", label: "창의성", icon: "ri-sparkling-2-line", color: "#FF8C00", targetScore: 82 },
  { key: "specificity", label: "구체성", icon: "ri-focus-3-line", color: "#7C3AED", targetScore: 74 },
  { key: "completeness", label: "완성도", icon: "ri-check-double-line", color: "#00D873", targetScore: 91 },
];

const AI_TIPS = [
  "\"cinematic lighting\"을 추가하면 더 극적인 분위기를 낼 수 있어요.",
  "배경 설명을 더 구체적으로 적으면 원하는 이미지에 가까워져요.",
  "--style raw 파라미터로 더 사실적인 결과를 얻을 수 있어요.",
  "색상 팔레트를 명시하면 일관된 톤앤매너를 유지할 수 있어요.",
];

export default function FeedbackStep({ prompt, classId, classTitle }: Props) {
  const navigate = useNavigate();
  const { addClassResult } = useGrowStore();
  const { scheduleReview } = useReviewStore();

  const [phase, setPhase] = useState<"loading" | "ai" | "reflecting" | "done">("loading");
  const [scores, setScores] = useState({ creativity: 0, specificity: 0, completeness: 0 });
  const [expertUnlocked, setExpertUnlocked] = useState(false);
  const [expertLoading, setExpertLoading] = useState(false);
  const [tipIdx] = useState(Math.floor(Math.random() * AI_TIPS.length));
  const [saved, setSaved] = useState(false);
  const [earnedPoints, setEarnedPoints] = useState(0);

  const finalScores = { creativity: 82, specificity: 74, completeness: 91 };
  const avgScore = Math.round(
    (finalScores.creativity + finalScores.specificity + finalScores.completeness) / 3
  );

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("ai"), 2200);
    return () => clearTimeout(t1);
  }, []);

  useEffect(() => {
    if (phase !== "ai") return;
    SCORE_ITEMS.forEach((item, idx) => {
      const target = finalScores[item.key as keyof typeof finalScores];
      const t = setTimeout(() => {
        let current = 0;
        const interval = setInterval(() => {
          current += 3;
          if (current >= target) {
            current = target;
            clearInterval(interval);
          }
          setScores((prev) => ({ ...prev, [item.key]: current }));
        }, 18);
      }, idx * 300);
      return () => clearTimeout(t);
    });
  }, [phase]);

  const displayAvg = Math.round(
    (scores.creativity + scores.specificity + scores.completeness) / 3
  );

  const handleExpert = () => {
    setExpertLoading(true);
    setTimeout(() => {
      setExpertLoading(false);
      setExpertUnlocked(true);
    }, 1800);
  };

  const handleSaveAndShare = () => {
    if (saved) return;
    const pts = calcPointsFromScore(avgScore, expertUnlocked);
    setEarnedPoints(pts);

    const now = Date.now();
    const result: ClassResult = {
      classId,
      classTitle,
      scores: finalScores,
      avgScore,
      pointsEarned: pts,
      expertVerified: expertUnlocked,
      completedAt: new Date(now).toLocaleDateString("ko-KR"),
      completedAtMs: now,
    };
    addClassResult(result);
    scheduleReview(classId, classTitle, now);
    setSaved(true);
    setPhase("reflecting");
  };

  // ── Reflection Modal ──
  if (phase === "reflecting") {
    return (
      <ReflectionModal
        classId={classId}
        classTitle={classTitle}
        avgScore={avgScore}
        onDone={() => setPhase("done")}
      />
    );
  }

  // ── Loading ──
  if (phase === "loading") {
    return (
      <div className="bg-white rounded-2xl p-12 text-center mb-6">
        <div className="relative w-20 h-20 mx-auto mb-6">
          <div className="w-20 h-20 rounded-full border-4 border-[#F2F4F6] absolute" />
          <div className="w-20 h-20 rounded-full border-4 border-t-[#00D873] border-r-transparent border-b-transparent border-l-transparent absolute animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <i className="ri-robot-2-line text-2xl text-[#00D873]" />
          </div>
        </div>
        <h3 className="text-xl font-extrabold text-[#191F28] mb-2">AI가 분석 중이에요</h3>
        <p className="text-[#8B95A1] text-sm">프롬프트의 창의성, 구체성, 완성도를 평가하고 있어요...</p>
        <div className="flex justify-center gap-1.5 mt-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="w-2 h-2 rounded-full bg-[#00D873] animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
          ))}
        </div>
      </div>
    );
  }

  // ── Completion Celebration ──
  if (phase === "done") {
    return (
      <div className="bg-white rounded-2xl p-10 text-center mb-6">
        {/* Coin burst anim */}
        <div className="relative inline-block mb-6">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#00D873]/20 to-[#00D873]/5 flex items-center justify-center mx-auto">
            <i className="ri-trophy-fill text-[#00D873] text-5xl" />
          </div>
          {/* Floating coins */}
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute w-7 h-7 rounded-full bg-gradient-to-br from-[#FFE566] to-[#FFA500] flex items-center justify-center text-xs font-black text-white"
              style={{
                top: "50%",
                left: "50%",
                animation: `coinOut${i} 0.8s ease-out forwards`,
              }}
            >G</div>
          ))}
        </div>

        <style>{`
          @keyframes coinOut0{0%{opacity:1;transform:translate(-50%,-50%) scale(1)}100%{opacity:0;transform:translate(calc(-50% + 60px),calc(-50% - 80px)) scale(0.5)}}
          @keyframes coinOut1{0%{opacity:1;transform:translate(-50%,-50%) scale(1)}100%{opacity:0;transform:translate(calc(-50% - 60px),calc(-50% - 80px)) scale(0.5)}}
          @keyframes coinOut2{0%{opacity:1;transform:translate(-50%,-50%) scale(1)}100%{opacity:0;transform:translate(calc(-50% + 80px),calc(-50%)) scale(0.5)}}
          @keyframes coinOut3{0%{opacity:1;transform:translate(-50%,-50%) scale(1)}100%{opacity:0;transform:translate(calc(-50% - 80px),calc(-50%)) scale(0.5)}}
          @keyframes coinOut4{0%{opacity:1;transform:translate(-50%,-50%) scale(1)}100%{opacity:0;transform:translate(calc(-50% + 40px),calc(-50% + 70px)) scale(0.5)}}
          @keyframes coinOut5{0%{opacity:1;transform:translate(-50%,-50%) scale(1)}100%{opacity:0;transform:translate(calc(-50% - 40px),calc(-50% + 70px)) scale(0.5)}}
        `}</style>

        <h2 className="text-2xl font-extrabold text-[#191F28] mb-2">클래스 완료! 🎉</h2>
        <p className="text-[#8B95A1] text-sm leading-relaxed mb-4">결과물이 피드에 공유됐어요.</p>

        {/* Points Earned Card */}
        <div className="bg-gradient-to-r from-[#E8F5E9] to-[#F0FFF7] rounded-2xl p-5 mb-6 inline-block w-full">
          <p className="text-xs font-bold text-[#00D873] mb-1">Grow Points 적립</p>
          <div className="text-3xl font-black text-[#191F28]">+{earnedPoints}P</div>
          {expertUnlocked && (
            <p className="text-xs text-[#8B95A1] mt-1">전문가 인증 보너스 +50P 포함</p>
          )}
          <p className="text-xs text-[#8B95A1] mt-0.5">대시보드에서 확인해보세요!</p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => navigate("/feed")}
            className="flex-1 bg-[#00D873] text-white font-bold py-4 rounded-2xl cursor-pointer hover:opacity-90 transition-all text-sm whitespace-nowrap"
            style={{ boxShadow: "0 4px 16px rgba(0,216,115,0.3)" }}
          >
            🎉 피드에서 자랑하기
          </button>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex-1 bg-[#F2F4F6] text-[#191F28] font-bold py-4 rounded-2xl cursor-pointer hover:bg-[#E8E9EB] transition-all text-sm whitespace-nowrap"
          >
            대시보드 확인 →
          </button>
        </div>
      </div>
    );
  }

  // ── AI Feedback ──
  return (
    <div className="mb-6 space-y-5">
      {/* Score Card */}
      <div className="bg-white rounded-2xl p-8 border-2 border-[#00D873]/30">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-sparkling-2-line text-[#00D873] text-lg" />
              </div>
              <span className="text-xs font-bold text-[#00D873] tracking-widest uppercase">AI 피드백</span>
            </div>
            <h2 className="text-2xl font-extrabold text-[#191F28]">프롬프트 분석 완료!</h2>
          </div>
          {/* Score Circle */}
          <div className="flex flex-col items-center flex-shrink-0">
            <div className="relative w-16 h-16">
              <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="26" fill="none" stroke="#F2F4F6" strokeWidth="6" />
                <circle cx="32" cy="32" r="26" fill="none" stroke="#00D873" strokeWidth="6"
                  strokeDasharray={`${(displayAvg / 100) * 163.4} 163.4`}
                  strokeLinecap="round"
                  style={{ transition: "stroke-dasharray 0.5s ease" }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[#191F28] font-extrabold text-base">{displayAvg}</span>
              </div>
            </div>
            <span className="text-xs text-[#8B95A1] mt-1 font-semibold">종합 점수</span>
          </div>
        </div>

        {/* Score Bars */}
        <div className="space-y-5 mb-7">
          {SCORE_ITEMS.map((item) => (
            <div key={item.key}>
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-1.5 text-sm font-bold text-[#191F28]">
                  <span className="w-5 h-5 flex items-center justify-center rounded-md" style={{ backgroundColor: `${item.color}22` }}>
                    <i className={`${item.icon} text-xs`} style={{ color: item.color }} />
                  </span>
                  {item.label}
                </span>
                <span className="text-sm font-extrabold" style={{ color: item.color }}>
                  {scores[item.key as keyof typeof scores]}점
                </span>
              </div>
              <div className="h-2.5 bg-[#F2F4F6] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700 ease-out"
                  style={{ width: `${scores[item.key as keyof typeof scores]}%`, backgroundColor: item.color }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* AI Comment */}
        <div className="bg-gradient-to-r from-[#E8F5E9] to-[#F0FFF7] rounded-xl p-4 flex gap-3">
          <div className="w-8 h-8 rounded-full bg-[#00D873] flex items-center justify-center flex-shrink-0">
            <i className="ri-robot-2-line text-white text-sm" />
          </div>
          <div>
            <p className="text-xs font-bold text-[#00D873] mb-1">GrowSeo AI</p>
            <p className="text-sm text-[#191F28] leading-relaxed font-medium">
              훌륭한 프롬프트입니다! 기본기가 탄탄하게 잡혀있어요.
            </p>
            <div className="mt-2.5 flex items-start gap-2 bg-white rounded-lg p-2.5">
              <span className="flex-shrink-0">💡</span>
              <p className="text-xs text-[#3D4756] leading-relaxed">{AI_TIPS[tipIdx]}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Expert Verification */}
      {!expertUnlocked ? (
        <div className="bg-white rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FFD600] to-[#FF8C00] flex items-center justify-center flex-shrink-0">
              <i className="ri-user-star-line text-white text-2xl" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-extrabold text-[#191F28] mb-0.5">전문가 2차 인증</p>
              <p className="text-xs text-[#8B95A1] leading-relaxed">
                담당 강사 검토 → 인증 마크 발급 (+50P 보너스)
              </p>
            </div>
            <button
              onClick={handleExpert}
              disabled={expertLoading}
              className="bg-[#191F28] text-white text-xs font-bold px-5 py-2.5 rounded-full cursor-pointer hover:bg-[#2D3748] transition-colors whitespace-nowrap flex-shrink-0 flex items-center gap-1.5"
            >
              {expertLoading ? <><i className="ri-loader-4-line animate-spin" />요청 중</> : "인증 요청하기"}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-r from-[#FFF9E6] to-[#FFFBE8] rounded-2xl p-6 border-2 border-[#FFD600]/40">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FFD600] to-[#FF8C00] flex items-center justify-center flex-shrink-0 relative">
              <i className="ri-medal-2-fill text-white text-2xl" />
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#00D873] flex items-center justify-center">
                <i className="ri-check-line text-white text-xs" />
              </div>
            </div>
            <div>
              <p className="text-sm font-extrabold text-[#191F28] mb-0.5 flex items-center gap-2">
                전문가 인증 완료! 🎉
                <span className="text-xs bg-[#FFD600] text-[#191F28] px-2 py-0.5 rounded-full font-bold">+50P</span>
              </p>
              <p className="text-xs text-[#8B95A1]">김준호 강사 · AI 프롬프트 전문가</p>
              <p className="text-xs text-[#3D4756] mt-1.5 italic">
                "스타일과 분위기 설정이 특히 훌륭해요!"
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Save & Share Button */}
      <button
        onClick={handleSaveAndShare}
        className="w-full py-5 bg-[#00D873] text-white font-extrabold text-[17px] rounded-2xl cursor-pointer hover:opacity-90 transition-all hover:-translate-y-0.5 whitespace-nowrap"
        style={{ boxShadow: "0 6px 24px rgba(0,216,115,0.35)" }}
      >
        🎉 완료! 포인트 적립하고 피드에 자랑하기
        <span className="ml-2 text-sm font-bold opacity-80">
          +{calcPointsFromScore(avgScore, expertUnlocked)}P 예정
        </span>
      </button>
    </div>
  );
}
